import { useMemo } from "react";
import * as THREE from "three";
import { Text, Html } from "@react-three/drei";
import { trackCurve, trackLength, stations, zones, getTrackFrame, hazards, hazardMinRadius } from "./track";
import { useGame, MODES } from "./store";

function TrackSegment({ curve }) {
  // Main track body — sleek steel-grey tube
  const tubeGeom = useMemo(
    () => new THREE.TubeGeometry(curve, 350, 0.5, 10, false),
    [curve],
  );

  // Twin steel rails — elevated above the tube
  const railLeft = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= 400; i++) {
      const t = i / 400;
      const { point, right, up } = getTrackFrame(curve, Math.min(0.999, t));
      pts.push(point.clone()
        .add(right.clone().multiplyScalar(1.3))
        .add(up.clone().multiplyScalar(0.45)));
    }
    return new THREE.CatmullRomCurve3(pts);
  }, [curve]);
  const railRight = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= 400; i++) {
      const t = i / 400;
      const { point, right, up } = getTrackFrame(curve, Math.min(0.999, t));
      pts.push(point.clone()
        .sub(right.clone().multiplyScalar(1.3))
        .add(up.clone().multiplyScalar(0.45)));
    }
    return new THREE.CatmullRomCurve3(pts);
  }, [curve]);

  // Emissive stripe along the centre (maglev style)
  const centerLine = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= 400; i++) {
      const t = i / 400;
      const { point, up } = getTrackFrame(curve, Math.min(0.999, t));
      pts.push(point.clone().add(up.clone().multiplyScalar(0.52)));
    }
    return new THREE.CatmullRomCurve3(pts);
  }, [curve]);

  const railLGeom  = useMemo(() => new THREE.TubeGeometry(railLeft,  350, 0.14, 7, false), [railLeft]);
  const railRGeom  = useMemo(() => new THREE.TubeGeometry(railRight, 350, 0.14, 7, false), [railRight]);
  const centerGeom = useMemo(() => new THREE.TubeGeometry(centerLine, 350, 0.07, 5, false), [centerLine]);

  // Cross ties every ~12 units
  const ties = useMemo(() => {
    const out = [];
    const N = 90;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const { point, right, up } = getTrackFrame(curve, Math.min(0.999, t));
      out.push({ point, right, up });
    }
    return out;
  }, [curve]);

  return (
    <group>
      {/* Main tube — concrete/steel grey */}
      <mesh geometry={tubeGeom} castShadow receiveShadow>
        <meshStandardMaterial color="#78828c" metalness={0.55} roughness={0.35} />
      </mesh>
      {/* Steel rails — bright metallic */}
      <mesh geometry={railLGeom} castShadow>
        <meshStandardMaterial color="#b8c4cc" metalness={0.92} roughness={0.12} />
      </mesh>
      <mesh geometry={railRGeom} castShadow>
        <meshStandardMaterial color="#b8c4cc" metalness={0.92} roughness={0.12} />
      </mesh>
      {/* Emissive centre stripe — cyan maglev glow */}
      <mesh geometry={centerGeom}>
        <meshStandardMaterial color="#00e5ff" emissive="#006080" emissiveIntensity={2.5} metalness={0.1} roughness={0.5} />
      </mesh>
      {/* Wooden cross ties */}
      {ties.map(({ point, right, up }, i) => {
        const pos = point.toArray();
        const tieRight = right.clone().multiplyScalar(1.8);
        return (
          <mesh key={i} position={[pos[0], pos[1] + 0.1, pos[2]]} castShadow>
            <boxGeometry args={[3.2, 0.2, 0.45]} />
            <meshStandardMaterial color="#5c3d1e" roughness={0.92} />
          </mesh>
        );
      })}
    </group>
  );
}

function StationMarker({ position, label, stationIndex }) {
  return (
    <group position={position}>
      {/* Tall pole */}
      <mesh position={[0, 4, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.1, 8, 8]} />
        <meshStandardMaterial color="#e0e0e0" />
      </mesh>
      {/* Glowing ring */}
      <mesh position={[0, 7, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.5, 0.15, 12, 32]} />
        <meshStandardMaterial
          color="#00ffaa"
          emissive="#00ffaa"
          emissiveIntensity={2}
        />
      </mesh>
      <Text
        position={[0, 9, 0]}
        fontSize={0.8}
        color="#00ffaa"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.04}
        outlineColor="#000"
      >
        {label}
      </Text>
    </group>
  );
}

/**
 * Vertical climb ladder dropped from the track at the station, reaching the ground.
 * Players walk to the bottom and press C to climb up.
 * Includes a tall vertical beacon visible from far away.
 */
function StationLadder({ trackPos, label }) {
  const groundY = -2.0;
  const trackY = trackPos.y;
  const height = Math.max(2, trackY - groundY);
  const midY = (groundY + trackY) / 2;
  const x = trackPos.x + 1.6;
  const z = trackPos.z;
  return (
    <group position={[x, midY, z]}>
      {/* Two vertical rails */}
      <mesh position={[0, 0, 0.25]} castShadow>
        <cylinderGeometry args={[0.06, 0.06, height, 8]} />
        <meshStandardMaterial color="#8a5a2a" />
      </mesh>
      <mesh position={[0, 0, -0.25]} castShadow>
        <cylinderGeometry args={[0.06, 0.06, height, 8]} />
        <meshStandardMaterial color="#8a5a2a" />
      </mesh>
      {/* Rungs */}
      {Array.from({ length: Math.floor(height / 0.5) }).map((_, i) => {
        const y = -height / 2 + 0.25 + i * 0.5;
        return (
          <mesh key={i} position={[0, y, 0]} castShadow>
            <cylinderGeometry args={[0.04, 0.04, 0.6, 8]} />
            <meshStandardMaterial color="#a87040" />
          </mesh>
        );
      })}
      {/* Tall vertical beacon (sky pillar) so players see it from far away */}
      <mesh position={[0, height / 2 + 12, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 24, 8]} />
        <meshStandardMaterial
          color="#00ffaa"
          emissive="#00ffaa"
          emissiveIntensity={1.6}
          transparent
          opacity={0.85}
        />
      </mesh>
      {/* Big glowing green base pad on the ground */}
      <mesh position={[0, -height / 2 + 0.05, 0]}>
        <cylinderGeometry args={[1.6, 1.6, 0.12, 24]} />
        <meshStandardMaterial
          color="#00ffaa"
          emissive="#00ffaa"
          emissiveIntensity={1}
        />
      </mesh>
      {/* "CLIMB HERE" floating text above the base */}
      <Text
        position={[0, -height / 2 + 1.8, 0]}
        fontSize={0.55}
        color="#00ffaa"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.05}
        outlineColor="#000"
      >
        {`🪜  ${label}  · press C`}
      </Text>
    </group>
  );
}

export default function Track() {
  return (
    <group>
      <TrackSegment curve={trackCurve} color="#c97a2b" />

      {/* Stations */}
      {stations.map((st, i) => {
        const p = trackCurve.getPointAt(st.s);
        return (
          <group key={i}>
            <StationMarker
              position={[p.x, p.y + 0.6, p.z]}
              label={st.label}
              stationIndex={i}
            />
            <StationLadder trackPos={p} label={st.label} />
          </group>
        );
      })}

      {/* Zone Specific Visuals */}
      <Zone1Speedometer />
      <Zone3Markers />
      <Zone4Graph />
      <HazardSigns />
    </group>
  );
}

/** 3D warning boards at each hazard checkpoint.
 *  Before solving: shows "v_max = ?" — after the student computes it,
 *  the board displays their unlocked speed limit, like a real road sign. */
function HazardSigns() {
  const hazardVmax = useGame((s) => s.hazardVmax);
  const world = useGame((s) => s.world);
  const signs = useMemo(
    () =>
      hazards.map((hz) => {
        const { point, right } = getTrackFrame(trackCurve, hz.checkpointS);
        const geom = hazardMinRadius(hz);
        const pos = point.clone().add(right.clone().multiplyScalar(5));
        return { hz, geom, pos };
      }),
    [],
  );
  return (
    <group>
      {signs.map(({ hz, geom, pos }) => {
        const solved = hazardVmax[hz.id] != null;
        // g matches the worlds (both Earth & Jurassic use 10)
        const vmax = Math.sqrt(10 * geom.rMin * Math.cos(geom.slope));
        return (
          <group key={hz.id} position={[pos.x, pos.y + 4, pos.z]}>
            {/* Post */}
            <mesh position={[0, -2.5, 0]}>
              <boxGeometry args={[0.25, 5, 0.25]} />
              <meshStandardMaterial color="#555" />
            </mesh>
            {/* Board */}
            <mesh>
              <boxGeometry args={[7, 3.4, 0.3]} />
              <meshStandardMaterial color={solved ? "#0a3d2a" : "#3d2a0a"} />
            </mesh>
            <Text position={[0, 0.9, 0.2]} fontSize={0.55} color={solved ? "#00ffaa" : "#ffd23f"}>
              {`⚠ ${hz.label}`}
            </Text>
            <Text position={[0, 0.1, 0.2]} fontSize={0.5} color="#3eb6ff">
              {`r = ${geom.rMin.toFixed(0)} m`}
            </Text>
            <Text position={[0, -0.8, 0.2]} fontSize={0.75} color={solved ? "#00ffaa" : "#ff8a3d"}>
              {solved ? `v_max = ${vmax.toFixed(1)} m/s` : "v_max = ?"}
            </Text>
          </group>
        );
      })}
    </group>
  );
}

/** Phase 2 world objects: STOP LINE + barrels (Brake Zone) and the
 *  ENGINE OFF gantry at the Energy Gate climb. Only visible in Phase 2. */
function Phase2Markers() {
  const phase = useGame((s) => s.phase);
  if (phase !== "P2") return null;

  const lineF = getTrackFrame(trackCurve, 0.685);
  const gateF = getTrackFrame(trackCurve, 0.755);

  return (
    <group>
      {/* STOP LINE — red bar across the track */}
      <group position={[lineF.point.x, lineF.point.y + 0.1, lineF.point.z]}>
        <mesh rotation={[0, Math.atan2(lineF.tangent.x, lineF.tangent.z), 0]}>
          <boxGeometry args={[0.6, 0.25, 6]} />
          <meshStandardMaterial color="#ff2244" emissive="#7a0015" />
        </mesh>
        <Text position={[0, 3.2, 0]} fontSize={1.1} color="#ff4d6d" rotation={[0, Math.atan2(lineF.tangent.x, lineF.tangent.z) + Math.PI / 2, 0]}>
          ⛔ STOP LINE
        </Text>
        {/* Barrels just past the line */}
        {[2.5, 4.5, 6.5].map((d, i) => {
          const p = trackCurve.getPointAt((0.685 + d / trackLength) % 1);
          return (
            <mesh key={i} position={[p.x - lineF.point.x, p.y - lineF.point.y + 1, p.z - lineF.point.z]}>
              <cylinderGeometry args={[0.7, 0.7, 1.6, 12]} />
              <meshStandardMaterial color="#ff8a3d" />
            </mesh>
          );
        })}
      </group>

      {/* ENGINE OFF gantry at the Energy Gate */}
      <group position={[gateF.point.x, gateF.point.y, gateF.point.z]}>
        <mesh position={[-3.2, 2.5, 0]}>
          <boxGeometry args={[0.3, 5, 0.3]} />
          <meshStandardMaterial color="#555" />
        </mesh>
        <mesh position={[3.2, 2.5, 0]}>
          <boxGeometry args={[0.3, 5, 0.3]} />
          <meshStandardMaterial color="#555" />
        </mesh>
        <mesh position={[0, 5, 0]} rotation={[0, Math.atan2(gateF.tangent.x, gateF.tangent.z), 0]}>
          <boxGeometry args={[7, 1.4, 0.3]} />
          <meshStandardMaterial color="#3d0a14" />
        </mesh>
        <Text position={[0, 5, 0.4]} fontSize={0.7} color="#ff8a9b" rotation={[0, Math.atan2(gateF.tangent.x, gateF.tangent.z) + Math.PI / 2, 0]}>
          ⚠ ENGINE OFF — KE pays from here
        </Text>
      </group>
    </group>
  );
}

function Zone1Speedometer() {
  const v = useGame((s) => s.v);
  const p = trackCurve.getPointAt(0.09); // Midway in Zone 1 (launch straight)
  return (
    <group position={[p.x, p.y + 6, p.z - 10]}>
      <mesh>
        <boxGeometry args={[12, 6, 0.5]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <Text position={[0, 1.2, 0.3]} fontSize={1.2} color="#fff">
        SPEED ZONE
      </Text>
      <Text position={[0, -1, 0.3]} fontSize={2.5} color="#00ffaa">
        {v.toFixed(1)} m/s
      </Text>
    </group>
  );
}

function Zone3Markers() {
  const markers = useMemo(() => {
    const arr = [];
    // Use the Distance-Time Zone (ZONE_8 in new track)
    const zone4 = zones.find((z) => z.id === "ZONE_8");
    if (!zone4) return arr;
    const startLen = zone4.startS * trackLength;
    const endLen = zone4.endS * trackLength;
    for (let d = startLen; d <= endLen; d += 10) {
      arr.push(d);
    }
    return arr;
  }, []);

  return (
    <group>
      {markers.map((d, i) => {
        const s = d / trackLength;
        const { point, right } = getTrackFrame(trackCurve, s);
        // Place marker to the side of the track
        const pos = point.clone().add(right.clone().multiplyScalar(4));
        return (
          <group key={i} position={pos}>
            <mesh position={[0, 1, 0]}>
              <boxGeometry args={[0.2, 2, 0.2]} />
              <meshStandardMaterial color="#555" />
            </mesh>
            <mesh position={[0, 2.5, 0]}>
              <boxGeometry args={[2, 1, 0.1]} />
              <meshStandardMaterial color="#ddd" />
            </mesh>
            <Text position={[0, 2.5, 0.06]} fontSize={0.6} color="#000">
              {`${(d - markers[0]).toFixed(0)}m`}
            </Text>
          </group>
        );
      })}
    </group>
  );
}

function Zone4Graph() {
  const v = useGame((s) => s.v);
  const s = useGame((st) => st.s);
  const mode = useGame((st) => st.mode);
  const p = trackCurve.getPointAt(0.77); // Midway in Distance-Time Zone (ZONE_8)

  // HTML overlay must not bleed over menu / finish screens
  if (mode !== MODES.ON_TRACK) return null;

  // A simple floating HTML graph simulation
  return (
    <group position={[p.x - 15, p.y + 10, p.z]}>
      <Html transform scale={4}>
        <div style={{
          width: 300, 
          height: 200, 
          background: 'rgba(0,0,0,0.8)', 
          border: '2px solid #00ffaa', 
          borderRadius: 8,
          padding: 10,
          color: 'white',
          fontFamily: 'monospace'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#00ffaa' }}>LIVE TELEMETRY</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <div>Velocity (m/s)</div>
              <div style={{ width: '100%', height: '20px', background: '#333', position: 'relative' }}>
                <div style={{ width: `${Math.min(100, (v/40)*100)}%`, height: '100%', background: '#ff4444', transition: 'width 0.1s' }} />
              </div>
            </div>
            <div>
              <div>Distance (s)</div>
              <div style={{ width: '100%', height: '20px', background: '#333', position: 'relative' }}>
                <div style={{ width: `${s*100}%`, height: '100%', background: '#4444ff', transition: 'width 0.1s' }} />
              </div>
            </div>
          </div>
        </div>
      </Html>
    </group>
  );
}
